import React, { useEffect, useState, useRef } from "react";
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  addDoc,
  Timestamp,
} from "firebase/firestore";
import { db } from "./firebaseConfig";
import { useNavigate } from "react-router-dom";

const TypingTest = ({ user }) => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const wordContainerRef = useRef(null);
  const testDurationInSeconds = 30; // Define test duration as a constant

  // Refs for accumulating character counts synchronously
  const correctCharsRef = useRef(0);
  const incorrectCharsRef = useRef(0);
  const totalTypedCharsRef = useRef(0);
  const isTestCompletedRef = useRef(false); // Add a ref to track completion status synchronously

  // -------------- WORD LIST --------------
  // Enhanced word list with more varied and challenging words
  const wordList = [
    // Common words
    "the", "be", "to", "of", "and", "a", "in", "that", "have", "I",
    "it", "for", "not", "on", "with", "he", "as", "you", "do", "at",
    "this", "but", "his", "by", "from", "they", "we", "say", "her", "she",
    "or", "an", "will", "my", "one", "all", "would", "there", "their", "what",
    // Medium difficulty words
    "people", "time", "year", "good", "some", "could", "them", "see", "other", "than",
    "then", "now", "look", "only", "come", "its", "over", "think", "also", "back",
    "after", "use", "two", "how", "our", "work", "first", "well", "way", "even",
    "new", "want", "because", "any", "these", "give", "day", "most", "us", "into",
    // More challenging words
    "computer", "science", "technology", "education", "knowledge", "experience", "development",
    "programming", "algorithm", "function", "variable", "constant", "parameter", "return",
    "condition", "iteration", "recursion", "structure", "database", "network", "interface",
    "application", "software", "hardware", "system", "memory", "processor", "graphics",
    "keyboard", "monitor", "internet", "browser", "website", "download", "upload",
    "digital", "analog", "binary", "decimal", "hexadecimal", "encryption", "decryption",
    // Technical terms
    "javascript", "python", "react", "angular", "vue", "node", "express", "mongodb",
    "firebase", "cloud", "server", "client", "frontend", "backend", "fullstack",
    "responsive", "mobile", "desktop", "tablet", "device", "component", "module",
    "library", "framework", "package", "dependency", "version", "update", "install",
    "deploy", "build", "compile", "debug", "error", "exception", "try", "catch",
    "promise", "async", "await", "callback", "event", "listener", "handler", "state"
  ];

  // -------------- STATES --------------
  const [testStarted, setTestStarted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(testDurationInSeconds);
  const [currentWords, setCurrentWords] = useState([]);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [currentInput, setCurrentInput] = useState("");
  const [correctChars, setCorrectChars] = useState(0);
  const [incorrectChars, setIncorrectChars] = useState(0);
  const [completedWords, setCompletedWords] = useState(0);
  const [wpm, setWpm] = useState(null);
  const [accuracy, setAccuracy] = useState(100);
  const [currentCharIndex, setCurrentCharIndex] = useState(0);
  const [typingHistory, setTypingHistory] = useState([]);
  const [showCountdown, setShowCountdown] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [testCompleted, setTestCompleted] = useState(false); // New state to track if test is completed

  // Leaderboard states
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  // -------------- STYLES --------------
  const containerStyle = {
    backgroundImage: "url('/green1.jpg')",
    backgroundSize: "cover",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    minHeight: "100vh",
    margin: 0,
    padding: "20px",
    fontFamily: "'Roboto Mono', monospace",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  };

  const headerStyle = {
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    padding: "12px 24px",
    marginBottom: "20px",
    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
    fontSize: "20px",
    fontWeight: "bold",
    color: "#2d6a4f",
    borderRadius: "4px",
    width: "100%",
    maxWidth: "700px",
    textAlign: "center",
  };

  const testAreaStyle = {
    backgroundColor: "rgba(236, 249, 236, 0.95)",
    borderRadius: "12px",
    width: "100%",
    maxWidth: "700px",
    margin: "0 auto 30px auto",
    padding: "30px",
    textAlign: "center",
    boxShadow: "0 8px 20px rgba(0, 0, 0, 0.15)",
  };

  const headingStyle = {
    fontSize: "28px",
    marginBottom: "15px",
    color: "#2d6a4f",
    fontWeight: "bold",
    letterSpacing: "1px",
  };

  const timerContainerStyle = {
    position: "relative",
    width: "100%",
    maxWidth: "600px",
    margin: "15px auto",
  };

  const timerStyle = {
    fontSize: "48px",
    fontWeight: "bold",
    margin: "10px 0",
    color: "#2d6a4f",
  };

  const progressBarContainerStyle = {
    width: "100%",
    height: "10px",
    backgroundColor: "#e0e0e0",
    borderRadius: "5px",
    overflow: "hidden",
    margin: "10px 0",
  };

  const progressBarStyle = {
    height: "100%",
    backgroundColor: "#2d6a4f",
    width: `${(timeLeft / testDurationInSeconds) * 100}%`,
    transition: "width 0.3s linear",
  };

  const wordContainerStyle = {
    backgroundColor: "#fff",
    padding: "20px",
    borderRadius: "8px",
    margin: "20px auto",
    maxWidth: "600px",
    height: "170px",
    border: "1px solid #ddd",
    boxShadow: "inset 0 1px 3px rgba(0,0,0,0.1)",
    position: "relative",
    userSelect: "none",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    overflow: "hidden",
    lineHeight: "1.8",
    fontSize: "18px",
  };

  const inputStyle = {
    width: "100%",
    maxWidth: "600px",
    height: "50px",
    margin: "20px 0",
    fontSize: "18px",
    padding: "10px 15px",
    borderRadius: "8px",
    border: testStarted ? "2px solid #2d6a4f" : "2px solid #ccc",
    fontFamily: "'Roboto Mono', monospace",
    backgroundColor: testStarted ? "#fff" : "#f5f5f5",
    transition: "all 0.3s ease",
    outline: "none",
    boxShadow: testStarted ? "0 0 5px rgba(45, 106, 79, 0.3)" : "none",
  };

  const buttonStyle = {
    backgroundColor: "#2d6a4f",
    border: "none",
    color: "#fff",
    padding: "12px 24px",
    margin: "5px",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "600",
    transition: "all 0.3s ease",
    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
    "&:hover": {
      backgroundColor: "#1b4332",
      transform: "translateY(-2px)",
      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
    },
    "&:active": {
      transform: "translateY(1px)",
      boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
    },
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: "#555",
    marginTop: "20px",
  };

  const statsStyle = {
    display: "flex",
    justifyContent: "space-around",
    margin: "20px auto",
    maxWidth: "600px",
  };

  const statItemStyle = {
    textAlign: "center",
    padding: "10px",
    borderRadius: "8px",
    backgroundColor: "rgba(255, 255, 255, 0.7)",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    minWidth: "100px",
    transition: "all 0.3s ease",
  };

  const statValueStyle = {
    fontSize: "28px",
    fontWeight: "bold",
    color: "#2d6a4f",
    margin: "0 0 5px 0",
  };

  const statLabelStyle = {
    fontSize: "14px",
    color: "#555",
    textTransform: "uppercase",
    letterSpacing: "1px",
  };

  const resultStyle = {
    margin: "25px 0",
    padding: "15px",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    borderRadius: "8px",
    boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
    animation: "fadeIn 0.5s ease-in-out",
  };

  const leaderboardStyle = {
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    borderRadius: "12px",
    width: "100%",
    maxWidth: "700px",
    margin: "0 auto",
    padding: "25px",
    textAlign: "center",
    boxShadow: "0 8px 20px rgba(0, 0, 0, 0.15)",
  };

  const leaderboardHeaderStyle = {
    fontSize: "24px",
    marginBottom: "20px",
    color: "#2d6a4f",
    fontWeight: "bold",
    borderBottom: "2px solid #2d6a4f",
    paddingBottom: "10px",
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: "0",
    marginTop: "15px",
    boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
    borderRadius: "8px",
    overflow: "hidden",
  };

  const thStyle = {
    backgroundColor: "#f0f9f0",
    padding: "12px 8px",
    borderBottom: "2px solid #2d6a4f",
    color: "#2d6a4f",
    fontWeight: "bold",
    textAlign: "center",
  };

  const tdStyle = {
    padding: "10px 8px",
    borderBottom: "1px solid #eee",
    textAlign: "center",
  };

  const countdownOverlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  };

  const countdownNumberStyle = {
    fontSize: "120px",
    color: "#fff",
    fontWeight: "bold",
    animation: "pulse 1s infinite",
  };

  // -------------- INITIALIZE TEST --------------
  const generateWords = () => {
    const randomWords = [];
    for (let i = 0; i < 100; i++) {  // Generate more words to ensure we don't run out
      const randomIndex = Math.floor(Math.random() * wordList.length);
      randomWords.push(wordList[randomIndex]);
    }
    setCurrentWords(randomWords);
    setCurrentWordIndex(0);
    setCurrentCharIndex(0);
  };

  // -------------- HELPER: PROCESS A SINGLE TYPED WORD --------------
  const processTypedWord = (typedWord) => {
    const currentWord = currentWords[currentWordIndex];
    let localCorrect = 0;
    let localIncorrect = 0;

    // Compare each character
    for (let i = 0; i < typedWord.length; i++) {
      if (i < currentWord.length && typedWord[i] === currentWord[i]) {
        localCorrect++;
      } else {
        localIncorrect++;
      }
    }

    // Count missed characters at the end of the word
    if (typedWord.length < currentWord.length) {
      localIncorrect += currentWord.length - typedWord.length;
    }

    // Update our refs
    correctCharsRef.current += localCorrect;
    incorrectCharsRef.current += localIncorrect;
    totalTypedCharsRef.current += typedWord.length;
    
    // Also update state for live display
    setCorrectChars(correctCharsRef.current);
    setIncorrectChars(incorrectCharsRef.current);
    
    // Recalculate accuracy immediately
    const total = correctCharsRef.current + incorrectCharsRef.current;
    if (total > 0) {
      setAccuracy(Math.round((correctCharsRef.current / total) * 100));
    }

    // Add to typing history for analysis
    setTypingHistory(prev => [...prev, {
      word: currentWord,
      typed: typedWord,
      correct: localCorrect,
      incorrect: localIncorrect
    }]);

    // Move to next word and update word count
    setCurrentWordIndex(prevIndex => prevIndex + 1);
    setCurrentCharIndex(0);
    setCompletedWords(prev => prev + 1);

    // If we're at the end of the current word set, generate new words
    if (currentWordIndex === currentWords.length - 1) {
      generateWords();
    }
  };

  // -------------- FETCH LEADERBOARD --------------
  useEffect(() => {
    const fetchTopResults = async () => {
      setLoading(true);
      try {
        const resultsRef = collection(db, "typingTestResults");
        const q = query(resultsRef, orderBy("wpm", "desc")); // Remove limit here
        const querySnapshot = await getDocs(q);
        
        // Create a map to store highest WPM per user
        const userBestScores = new Map();
        
        querySnapshot.docs.forEach((doc) => {
          const result = { id: doc.id, ...doc.data() };
          const userId = result.uid;
          
          // Only store the highest WPM score for each user
          if (!userBestScores.has(userId) || userBestScores.get(userId).wpm < result.wpm) {
            userBestScores.set(userId, result);
          }
        });
        
        // Convert map to array and sort by WPM
        const topResults = Array.from(userBestScores.values())
          .sort((a, b) => b.wpm - a.wpm)
          .slice(0, 10); // Take top 10
        
        setResults(topResults);
      } catch (error) {
        console.error("Error fetching leaderboard:", error.message);
      }
      setLoading(false);
    };
    fetchTopResults();
    generateWords();
  }, []);

  // -------------- COUNTDOWN EFFECT --------------
  useEffect(() => {
    let countdownTimer;
    if (showCountdown && countdown > 0) {
      countdownTimer = setTimeout(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
    } else if (showCountdown && countdown === 0) {
      setShowCountdown(false);
      startTest();
    }
    return () => clearTimeout(countdownTimer);
  }, [showCountdown, countdown]);

  // -------------- TIMER EFFECT --------------
  useEffect(() => {
    let timerId;
    if (testStarted && timeLeft > 0) {
      timerId = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timerId);
            finishTest(true); // Pass true to indicate test ended due to time up
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (timerId) clearInterval(timerId);
    };
  }, [testStarted]);

  // -------------- FOCUS INPUT WHEN TEST STARTS --------------
  useEffect(() => {
    if (testStarted && inputRef.current) {
      inputRef.current.focus();
    }
  }, [testStarted]);

  // -------------- START COUNTDOWN --------------
  const startCountdown = () => {
    setShowCountdown(true);
    setCountdown(3);
    setTestCompleted(false); // Reset test completion status
    isTestCompletedRef.current = false; // Reset the ref as well
  };

  // -------------- START TEST --------------
  const startTest = () => {
    generateWords();
    setTestStarted(true);
    setTimeLeft(testDurationInSeconds);
    setCurrentInput("");
    setCurrentWordIndex(0);
    setCurrentCharIndex(0);
    setCorrectChars(0);
    setIncorrectChars(0);
    setCompletedWords(0);
    setWpm(null);
    setAccuracy(100);
    setTypingHistory([]);
    setTestCompleted(false);
    
    // Reset refs as well
    correctCharsRef.current = 0;
    incorrectCharsRef.current = 0;
    totalTypedCharsRef.current = 0;
    isTestCompletedRef.current = false; // Reset the completion status ref
    
    // Focus the input field
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // -------------- HANDLE INPUT CHANGE --------------
  const handleInputChange = (e) => {
    if (!testStarted || testCompleted) return;
    
    const value = e.target.value;
    const lastChar = value.slice(-1);

    // Update current character index for cursor position
    setCurrentCharIndex(value.length);

    if (lastChar === " " && value.trim() !== "") {
      processTypedWord(value.trim());
      setCurrentInput("");
    } else {
      setCurrentInput(value);
    }
  };

  // -------------- HANDLE KEY DOWN --------------
  const handleKeyDown = (e) => {
    // Prevent tab from changing focus
    if (e.key === "Tab") {
      e.preventDefault();
    }
  };

  // -------------- CALCULATE WPM --------------
  const calculateWPM = (isTimeUp = false) => {
    // Use the full test duration when time is up, otherwise use elapsed time
    const secondsUsed = isTimeUp ? testDurationInSeconds : Math.max(1, testDurationInSeconds - timeLeft);
    const minutesUsed = secondsUsed / 60;
    
    // Standard: 5 chars = 1 word
    const wordsTyped = correctCharsRef.current / 5;
    
    // Cap the WPM at a reasonable maximum (300 is already superhuman)
    return Math.min(300, Math.round(wordsTyped / minutesUsed));
  };

  // -------------- FINISH TEST --------------
  const finishTest = async (isTimeUp = false) => {
    // If the test is already completed, prevent duplicate processing
    // Use the ref for synchronous checking to prevent race conditions
    if (isTestCompletedRef.current) return;
    
    // Immediately mark the test as completed
    isTestCompletedRef.current = true;
    setTestCompleted(true);
    setTestStarted(false);
    
    // Process any leftover word
    if (currentInput.trim()) {
      processTypedWord(currentInput.trim());
      setCurrentInput("");
    }

    // Calculate final WPM - important fix here to use the correct time
    const calculatedWPM = calculateWPM(isTimeUp);
    setWpm(calculatedWPM);

    // Final accuracy calculation
    const totalChars = correctCharsRef.current + incorrectCharsRef.current;
    const finalAccuracy = totalChars > 0
      ? Math.round((correctCharsRef.current / totalChars) * 100)
      : 100;
    setAccuracy(finalAccuracy);

    // Create a unique identifier for this test session to prevent duplicates
    const testSessionId = `${user?.uid || 'guest'}-${Date.now()}`;
    
    // Save result to Firestore if user is logged in
    if (user?.uid) {
      try {
        // Use a unique ID for this submission to prevent duplicates
        const resultRef = collection(db, "typingTestResults");
        
        // Add document with the calculated values
        await addDoc(resultRef, {
          uid: user.uid,
          username: user.username || user.displayName || user.email || "Unknown User",
          wpm: calculatedWPM,
          accuracy: finalAccuracy,
          timestamp: Timestamp.now(),
          charsTyped: totalTypedCharsRef.current,
          wordsCompleted: completedWords,
          testSessionId // Add a unique session ID
        });
        
        // Refresh leaderboard
        const q = query(resultRef, orderBy("wpm", "desc")); // Remove limit here
        const querySnapshot = await getDocs(q);
        
        // Create a map to store highest WPM per user
        const userBestScores = new Map();
        
        querySnapshot.docs.forEach((doc) => {
          const result = { id: doc.id, ...doc.data() };
          const userId = result.uid;
          
          // Only store the highest WPM score for each user
          if (!userBestScores.has(userId) || userBestScores.get(userId).wpm < result.wpm) {
            userBestScores.set(userId, result);
          }
        });
        
        // Convert map to array and sort by WPM
        const topResults = Array.from(userBestScores.values())
          .sort((a, b) => b.wpm - a.wpm)
          .slice(0, 10); // Take top 10
        
        setResults(topResults);
      } catch (error) {
        console.error("Error saving typing test result:", error.message);
      }
    } else {
      console.warn("User is not logged in; record not saved.");
    }

    setTimeLeft(0);
  };

  // -------------- DISABLE PASTE --------------
  const handlePaste = (e) => {
    e.preventDefault();
    alert("Pasting is disabled. Please type manually.");
  };

  // -------------- RENDER WORDS --------------
  const renderWords = () => {
    const wordsPerLine = 6; // Approximate words per line
    const linesToShow = 3; // Number of lines to show at once
    const totalWordsToShow = wordsPerLine * linesToShow;
    
    // Calculate which "page" of words we're on
    const currentPage = Math.floor(currentWordIndex / totalWordsToShow);
    const startIdx = currentPage * totalWordsToShow;
    const endIdx = startIdx + totalWordsToShow;
    
    const commonWordStyle = {
      fontWeight: "normal",
      padding: "4px 8px",
      margin: "0 4px",
      borderRadius: "4px",
      display: "inline-block",
      minWidth: "30px",
      height: "32px",
      lineHeight: "24px",
    };

    return (
      <div style={{ 
        position: "relative",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "flex-start",
        gap: "4px",
        height: "150px",
        overflow: "hidden",
        padding: "8px",
        transition: "opacity 0.2s ease",
      }}>
        {currentWords.slice(startIdx, endIdx).map((word, index) => {
          const actualIndex = index + startIdx;
          const isCurrent = actualIndex === currentWordIndex;
          const isPast = actualIndex < currentWordIndex;
          
          if (isCurrent) {
            return (
              <span
                key={actualIndex}
                style={{
                  ...commonWordStyle,
                  fontWeight: "bold",
                  backgroundColor: "#fffacd",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
              >
                {word.split('').map((char, charIdx) => {
                  let charStyle = {};
                  
                  if (charIdx < currentInput.length) {
                    if (char === currentInput[charIdx]) {
                      charStyle = { color: "#2d6a4f", fontWeight: "bold" };
                    } else {
                      charStyle = { color: "#d32f2f", fontWeight: "bold", textDecoration: "underline" };
                    }
                  } else if (charIdx === currentInput.length) {
                    charStyle = { 
                      backgroundColor: "#2d6a4f", 
                      color: "white",
                      borderRadius: "2px",
                    };
                  }
                  
                  return (
                    <span key={charIdx} style={charStyle}>
                      {char}
                    </span>
                  );
                })}
              </span>
            );
          }
          
          return (
            <span
              key={actualIndex}
              style={{
                ...commonWordStyle,
                color: isPast ? "#2d6a4f" : "#666",
                opacity: isPast ? 0.7 : 1,
                backgroundColor: isPast ? "rgba(45, 106, 79, 0.1)" : "transparent",
              }}
            >
              {word}
            </span>
          );
        })}
      </div>
    );
  };

  // -------------- REAL-TIME WPM (IN RENDER) --------------
  const getRealtimeWPM = () => {
    if (!testStarted || testCompleted) return wpm || 0;
    
    // Calculate real-time WPM during the test
    return calculateWPM(false);
  };

  // -------------- RENDER COUNTDOWN OVERLAY --------------
  const renderCountdownOverlay = () => {
    if (!showCountdown) return null;
    
    return (
      <div style={countdownOverlayStyle}>
        <div style={countdownNumberStyle}>{countdown}</div>
      </div>
    );
  };

  // -------------- RENDER RESULT DETAILS --------------
  const renderResultDetails = () => {
    if (testStarted || wpm === null) return null;
    
    // Calculate additional statistics
    const totalChars = correctCharsRef.current + incorrectCharsRef.current;
    const charsPerSecond = totalChars / testDurationInSeconds;
    
    return (
      <div style={resultStyle}>
        <h3 style={{ color: "#2d6a4f", marginBottom: "15px" }}>Your Results</h3>
        
        <div style={{ display: "flex", justifyContent: "space-around", flexWrap: "wrap", gap: "10px" }}>
          <div style={{ textAlign: "center", padding: "10px", minWidth: "120px" }}>
            <div style={{ fontSize: "32px", fontWeight: "bold", color: "#2d6a4f" }}>{wpm}</div>
            <div style={{ fontSize: "14px", color: "#555" }}>WORDS PER MINUTE</div>
          </div>
          
          <div style={{ textAlign: "center", padding: "10px", minWidth: "120px" }}>
            <div style={{ fontSize: "32px", fontWeight: "bold", color: "#2d6a4f" }}>{accuracy}%</div>
            <div style={{ fontSize: "14px", color: "#555" }}>ACCURACY</div>
          </div>
          
          <div style={{ textAlign: "center", padding: "10px", minWidth: "120px" }}>
            <div style={{ fontSize: "32px", fontWeight: "bold", color: "#2d6a4f" }}>{completedWords}</div>
            <div style={{ fontSize: "14px", color: "#555" }}>WORDS COMPLETED</div>
          </div>
          
          <div style={{ textAlign: "center", padding: "10px", minWidth: "120px" }}>
            <div style={{ fontSize: "32px", fontWeight: "bold", color: "#2d6a4f" }}>{charsPerSecond.toFixed(1)}</div>
            <div style={{ fontSize: "14px", color: "#555" }}>CHARS PER SECOND</div>
          </div>
        </div>
        
        <button style={{...buttonStyle, marginTop: "20px"}} onClick={startCountdown}>
          Try Again
        </button>
      </div>
    );
  };

  const formatUsername = (fullUsername) => {
    if (!fullUsername) return "Unknown User";
    
    // Split the username into parts
    const parts = fullUsername.split(' ');
    
    // If there's only one part, return it as is
    if (parts.length === 1) return parts[0];
    
    // Get first name and first initial of last name
    const firstName = parts[0];
    const lastInitial = parts[parts.length - 1][0];
    
    return `${firstName} ${lastInitial}.`;
  };

  // -------------- RENDER --------------
  return (
    <div style={containerStyle}>
      {renderCountdownOverlay()}
      
      <div style={headerStyle}>Are you the fastest typer!?</div>
      
      <div style={testAreaStyle}>
        <h2 style={headingStyle}>30-SECOND TYPING SPEED TEST</h2>
        
        <div style={timerContainerStyle}>
          <div style={timerStyle}>{timeLeft > 0 ? timeLeft : "--"}</div>
          
          {testStarted && (
            <div style={progressBarContainerStyle}>
              <div style={progressBarStyle}></div>
            </div>
          )}
        </div>
        
        <div style={statsStyle}>
          <div style={{
            ...statItemStyle,
            transform: testStarted ? 'scale(1.05)' : 'scale(1)',
            backgroundColor: testStarted ? 'rgba(45, 106, 79, 0.1)' : 'rgba(255, 255, 255, 0.7)'
          }}>
            <div style={statValueStyle}>
              {getRealtimeWPM()}
            </div>
            <div style={statLabelStyle}>WPM</div>
          </div>
          
          <div style={statItemStyle}>
            <div style={statValueStyle}>{accuracy}%</div>
            <div style={statLabelStyle}>Accuracy</div>
          </div>
          
          <div style={statItemStyle}>
            <div style={statValueStyle}>{completedWords}</div>
            <div style={statLabelStyle}>Words</div>
          </div>
        </div>
        
        <div ref={wordContainerRef} style={wordContainerStyle}>
          {renderWords()}
        </div>
        
        <input
          ref={inputRef}
          type="text"
          style={inputStyle}
          value={currentInput}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          disabled={!testStarted || testCompleted}
          placeholder={testStarted ? "Type here..." : "Press Start to begin typing..."}
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck="false"
        />
        
        <div>
          {/* Start Button (only if not started yet and timeLeft=30) */}
          {!testStarted && timeLeft === testDurationInSeconds && !showCountdown && !testCompleted && (
            <button style={buttonStyle} onClick={startCountdown}>
              Start Test
            </button>
          )}
          
          {/* Show results after test is done */}
          {renderResultDetails()}
        </div>
        
        <button onClick={() => navigate("/dashboard")} style={secondaryButtonStyle}>
          Back to Dashboard
        </button>
      </div>
      
      <div style={leaderboardStyle}>
        <h3 style={leaderboardHeaderStyle}>Top 10 Fastest Typists</h3>
        {loading ? (
          <p>Loading leaderboard...</p>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: "10%" }}>Rank</th>
                <th style={{ ...thStyle, width: "35%" }}>Username</th>
                <th style={{ ...thStyle, width: "15%" }}>WPM</th>
                <th style={{ ...thStyle, width: "15%" }}>Accuracy</th>
                <th style={{ ...thStyle, width: "25%" }}>Date/Time</th>
              </tr>
            </thead>
            <tbody>
              {results.map((result, index) => (
                <tr
                  key={result.id}
                  style={{
                    backgroundColor:
                      user?.uid === result.uid
                        ? "rgba(45, 106, 79, 0.1)"
                        : index % 2 === 0
                        ? "#fff"
                        : "#f9f9f9",
                  }}
                >
                  <td style={{ ...tdStyle, fontWeight: "bold" }}>#{index + 1}</td>
                  <td style={tdStyle}>{formatUsername(result.username)}</td>
                  <td style={{ ...tdStyle, fontWeight: "bold", color: "#2d6a4f" }}>
                    {result.wpm}
                  </td>
                  <td style={tdStyle}>{result.accuracy || "N/A"}%</td>
                  <td style={tdStyle}>
                    {result.timestamp
                      ? new Date(result.timestamp.seconds * 1000).toLocaleString(undefined, {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "N/A"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default TypingTest;
